import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { ddm, ddmSv } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { navigate } from '../router.js';
import type { MyWorkRow } from './Home.js';
import { KindIcon } from './WeekCalendar.js';
import { mapsHref } from './shootPrep.js';
import type { HiddenProject } from './hideProject.js';

/**
 * "Idag" for editors, camera and freelancers (owner 2026-10-20, "God morgon Nils"): the week calendar stays on top (the
 * page puts it there), and under it today's shoots and deadlines, the viewer's turn, what is new since yesterday and the
 * coming seven days. One call (/api/crew/today); the same for employees and freelancers. No capacity figures.
 */

interface CrewEvent {
  uid: string;
  date: string;
  start: string | null;
  end: string | null;
  kind: 'shoot' | 'first_draft' | 'next_draft' | 'internal_deadline';
  title: string;
  projectId: string;
  projectName: string;
  location: string;
  note: string;
}
interface NewsItem {
  id: string;
  text: string;
  where: string;
  link: string | null;
  at: string;
  read: boolean;
}
export interface TodayView {
  today: string;
  todayEvents: CrewEvent[];
  coming: CrewEvent[];
  yourTurn: MyWorkRow[] | null;
  upNext: MyWorkRow[] | null;
  /** projects the viewer hid from My work (with the turn; older API versions leave it out) */
  hidden?: HiddenProject[] | null;
  /** videos out with someone else (with the turn; older API versions leave it out) */
  waiting?: { by: { who: string; n: number }[]; rows: MyWorkRow[] } | null;
  news: NewsItem[];
  moreNews: number;
}

const TX = texts(
  {
    morning: (n: string) => `Good morning ${n}`,
    day: (n: string) => `Hi ${n}`,
    evening: (n: string) => `Good evening ${n}`,
    today: 'Today',
    nothingToday: 'Nothing booked today.',
    allDay: 'All day',
    shoot: 'Shoot',
    turn: 'Your turn',
    upNext: 'Up next',
    noTurn: 'Nothing waits for you right now.',
    news: 'New since yesterday',
    noNews: 'Nothing new since yesterday.',
    more: (n: number) => `+${n} more in the bell`,
    coming: 'Coming 7 days',
    shoots: 'Your shoots',
    noShoots: 'No shoots in the coming week.',
    brief: 'Brief',
    prep: 'Before the shoot',
    openShoot: (p: string) => `Open the shoots in ${p}`,
    noComing: 'Nothing in the coming week.',
    loading: 'Loading today…',
    failed: 'Today could not be loaded – try again in a moment.',
    summary: (shoots: number, turn: number) =>
      [shoots ? `${shoots} shoot${shoots === 1 ? '' : 's'} today` : '', turn ? `${turn} video${turn === 1 ? '' : 's'} waiting for you` : ''].filter(Boolean).join(' · ') || 'Nothing waiting for you today'
  },
  {
    morning: (n: string) => `God morgon ${n}`,
    day: (n: string) => `Hej ${n}`,
    evening: (n: string) => `God kväll ${n}`,
    today: 'I dag',
    nothingToday: 'Inget inbokat i dag.',
    allDay: 'Heldag',
    shoot: 'Inspelning',
    turn: 'Din tur',
    upNext: 'Härnäst',
    noTurn: 'Inget väntar på dig just nu.',
    news: 'Nytt sedan i går',
    noNews: 'Inget nytt sedan i går.',
    more: (n: number) => `+${n} till i klockan`,
    coming: 'Kommande 7 dagar',
    shoots: 'Dina inspelningar',
    noShoots: 'Inga inspelningar den närmaste veckan.',
    brief: 'Brief',
    prep: 'Inför inspelningen',
    openShoot: (p: string) => `Öppna inspelningarna i ${p}`,
    noComing: 'Inget den närmaste veckan.',
    loading: 'Laddar dagen…',
    failed: 'Dagen kunde inte laddas – försök igen om en stund.',
    summary: (shoots: number, turn: number) =>
      [shoots ? `${shoots} ${shoots === 1 ? 'inspelning' : 'inspelningar'} i dag` : '', turn ? `${turn} ${turn === 1 ? 'video väntar' : 'videor väntar'} på dig` : ''].filter(Boolean).join(' · ') || 'Inget väntar på dig i dag'
  }
);

const stockholmHour = () => Number(new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Stockholm', hour: '2-digit', hourCycle: 'h23' }).format(new Date()));

/** Editors and camera (not producers, CD, sales or admin) get "Idag" and the board; so does "Visa som" a role like that. */
export const isCrewView = (roles: string[]) => roles.some((r) => r === 'editor' || r === 'camera' || r === 'freelancer') && !roles.some((r) => r === 'admin' || r === 'producer' || r === 'cd' || r === 'sales');

/** "God morgon Nils" before 10, "Hej Nils" during the day, "God kväll Nils" from 18. */
export function greeting(name: string, lang: Lang, hour = stockholmHour()): string {
  const t = TX[lang];
  const first = name.split(' ')[0] ?? name;
  return hour < 10 ? t.morning(first) : hour < 18 ? t.day(first) : t.evening(first);
}

/** The line under the greeting: today's shoots and the videos waiting (the turn is counted only when it was asked for). */
export function todaySummary(v: TodayView | null, lang: Lang): string {
  if (!v) return '';
  return TX[lang].summary(v.todayEvents.filter((e) => e.kind === 'shoot').length, v.yourTurn?.length ?? 0);
}

/** `on` false = not for this viewer (no call is made). */
export function useToday(api: Api, turn: boolean, on = true, rev = 0): TodayView | null | 'off' {
  const [v, setV] = useState<TodayView | null | 'off'>(() => (on ? (peek<TodayView>(api, `/api/crew/today${turn ? '' : '?turn=0'}`) ?? null) : 'off'));
  useEffect(() => {
    if (on) api.get<TodayView>(`/api/crew/today${turn ? '' : '?turn=0'}`).then(setV, () => setV('off'));
  }, [api, on, rev]);
  return v;
}

const when = (e: CrewEvent, lang: Lang) => (e.start ? `${e.start}${e.end ? `–${e.end}` : ''}` : e.kind === 'shoot' ? TX[lang].allDay : '');

/** A project opens on the staff page, or where the link page says (freelancers have no staff pages). */
export type OpenProject = (projectId: string) => void;
const staffOpen: OpenProject = (id) => navigate(`/projects/${id}`);

function EventRow(p: { e: CrewEvent; open: OpenProject; day?: string }) {
  const lang = useLang();
  const { e } = p;
  const sub = e.kind === 'shoot' ? [e.location, e.note].filter(Boolean).join(' · ') : e.projectName;
  // in the week list the date leads and the time joins the second line, so a narrow column keeps the title whole
  const time = when(e, lang);
  return (
    <li class={`td-row${e.kind === 'shoot' ? ' td-row--shoot' : ''}`}>
      {p.day !== undefined ? <span class="td-day">{p.day}</span> : <span class="td-time">{time}</span>}
      <KindIcon kind={e.kind === 'shoot' ? 'shoot' : 'edit'} />
      <span class="td-what">
        <button type="button" class="td-link" onClick={() => p.open(e.projectId)}>
          {e.title}
        </button>
        {(p.day !== undefined ? [time, sub] : [sub]).some(Boolean) ? <span class="small muted td-sub">{(p.day !== undefined ? [time, sub] : [sub]).filter(Boolean).join(' · ')}</span> : null}
      </span>
    </li>
  );
}

/** Today's shoots and deadlines. */
/** The day's digest could not be loaded: say so instead of leaving the page without it. */
export function TodayFailed() {
  return <p class="muted">{TX[useLang()].failed}</p>;
}

export function TodayNow(p: { v: TodayView; open?: OpenProject }) {
  const t = TX[useLang()];
  return (
    <section class="panel td-card" aria-labelledby="td-now">
      <h2 class="blk-title" id="td-now">
        {t.today}
      </h2>
      {p.v.todayEvents.length ? (
        <ul class="td-list">
          {p.v.todayEvents.map((e) => (
            <EventRow key={e.uid} e={e} open={p.open ?? staffOpen} />
          ))}
        </ul>
      ) : (
        <p class="muted small">{t.nothingToday}</p>
      )}
    </section>
  );
}

/** What is new since yesterday: the bell's own notices, at most five. */
export function TodayNews(p: { v: TodayView; openLink?: (link: string) => void }) {
  const t = TX[useLang()];
  const open = p.openLink ?? ((l: string) => navigate(l));
  return (
    <section class="panel td-card" aria-labelledby="td-news">
      <h2 class="blk-title" id="td-news">
        {t.news}
      </h2>
      {p.v.news.length ? (
        <ul class="td-list">
          {p.v.news.map((n) => (
            <li key={n.id} class={`td-row td-news${n.read ? '' : ' is-unread'}`}>
              <span class="td-what">
                {n.link ? (
                  <button type="button" class="td-link" onClick={() => open(n.link!)}>
                    {n.text}
                  </button>
                ) : (
                  <span>{n.text}</span>
                )}
                {n.where ? <span class="small muted td-sub">{n.where}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <p class="muted small">{t.noNews}</p>
      )}
      {p.v.moreNews ? <p class="small muted">{t.more(p.v.moreNews)}</p> : null}
    </section>
  );
}

/** The coming seven days, one line per date. */
export function TodayComing(p: { v: TodayView; open?: OpenProject }) {
  const lang = useLang();
  const t = TX[lang];
  const day = (k: string) => (lang === 'sv' ? ddmSv : ddm)(k);
  return (
    <section class="panel td-card" aria-labelledby="td-coming">
      <h2 class="blk-title" id="td-coming">
        {t.coming}
      </h2>
      {p.v.coming.length ? (
        <ul class="td-list">
          {p.v.coming.map((e, i) => (
            <EventRow key={e.uid} e={e} open={p.open ?? staffOpen} day={i === 0 || p.v.coming[i - 1]!.date !== e.date ? day(e.date) : ' '} />
          ))}
        </ul>
      ) : (
        <p class="muted small">{t.noComing}</p>
      )}
    </section>
  );
}

export const todayLoading = (lang: Lang) => TX[lang].loading;

/**
 * Kamera's main column (owner 2026-10-21): the viewer's shoots today and the coming seven days, each opening the project's
 * Shoots tab, with the brief one click away. The same events as the week above (/api/crew/today).
 */
export function TodayShoots(p: { v: TodayView }) {
  const lang = useLang();
  const t = TX[lang];
  const day = (k: string) => (k === p.v.today ? (lang === 'sv' ? 'i dag' : 'today') : (lang === 'sv' ? ddmSv : ddm)(k));
  const shoots = [...p.v.todayEvents, ...p.v.coming].filter((e) => e.kind === 'shoot');
  return (
    <section class="panel td-card" aria-labelledby="td-shoots" data-tour="shoots">
      <h2 class="blk-title" id="td-shoots">
        {t.shoots} {shoots.length ? <span class="seg-n">{shoots.length}</span> : null}
      </h2>
      {shoots.length ? (
        <ul class="td-list">
          {shoots.map((e, i) => (
            <li key={e.uid} class={`td-row td-row--shoot${e.date === p.v.today ? ' is-today' : ''}`}>
              <span class="td-day">{i === 0 || shoots[i - 1]!.date !== e.date ? day(e.date) : ' '}</span>
              <KindIcon kind="shoot" />
              <span class="td-what">
                <button type="button" class="td-link" aria-label={t.openShoot(e.projectName)} onClick={() => navigate(`/projects/${e.projectId}/shoots`)}>
                  {e.projectName}
                </button>
                <span class="small muted td-sub">
                  {[when(e, lang)].filter(Boolean).join('')}
                  {/* the address opens Maps (owner 2026-11-01): only the camera is on site */}
                  {e.location ? (
                    <>
                      {' · '}
                      <a href={mapsHref(e.location)} target="_blank" rel="noopener noreferrer">
                        {e.location}
                      </a>
                    </>
                  ) : null}
                  {e.note ? ` · ${e.note}` : ''}
                </span>
              </span>
              {/* a shoot booked in the Hub opens "Inför inspelningen" (owner 2026-11-01); Monday's shoot day keeps the brief */}
              {e.uid.startsWith('shoot-') ? (
                <a class="btn btn-sm td-brief" href={`/projects/${e.projectId}/shoots/${e.uid.slice(6)}`} onClick={(ev) => (ev.preventDefault(), navigate(`/projects/${e.projectId}/shoots/${e.uid.slice(6)}`))}>
                  {t.prep}
                </a>
              ) : (
                <a class="btn btn-sm td-brief" href={`/projects/${e.projectId}/brief`} onClick={(ev) => (ev.preventDefault(), navigate(`/projects/${e.projectId}/brief`))}>
                  {t.brief}
                </a>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p class="muted small">{t.noShoots}</p>
      )}
    </section>
  );
}
